import type { StreamEvent } from '@medicail/shared';

export interface SseMessage {
  event: string;
  data: string;
  id?: string;
}

/**
 * Incremental Server-Sent Events parser. Feed it decoded text chunks of any size
 * (chunk boundaries may fall mid line, mid event or between \r and \n).
 */
export class SseParser {
  private buffer = '';
  private eventName = '';
  private dataLines: string[] = [];
  private lastId: string | undefined;

  push(chunk: string): SseMessage[] {
    this.buffer += chunk;
    // A trailing \r may be the first half of \r\n; wait for more input.
    const hold = this.buffer.endsWith('\r') ? 1 : 0;
    const usable = this.buffer.slice(0, this.buffer.length - hold);
    const tail = this.buffer.slice(this.buffer.length - hold);

    const out: SseMessage[] = [];
    const lines = usable.split(/\r\n|\n|\r/);
    // Last element is an incomplete line (or empty when usable ended with a terminator).
    this.buffer = (lines.pop() ?? '') + tail;
    for (const line of lines) this.processLine(line, out);
    return out;
  }

  /** Call when the stream ends to flush a final unterminated event. */
  flush(): SseMessage[] {
    const out: SseMessage[] = [];
    if (this.buffer) {
      this.processLine(this.buffer.replace(/\r$/, ''), out);
      this.buffer = '';
    }
    this.dispatch(out);
    return out;
  }

  private processLine(line: string, out: SseMessage[]): void {
    if (line === '') {
      this.dispatch(out);
      return;
    }
    if (line.startsWith(':')) return; // comment / keep-alive
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    let value = idx === -1 ? '' : line.slice(idx + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        this.eventName = value;
        break;
      case 'data':
        this.dataLines.push(value);
        break;
      case 'id':
        this.lastId = value;
        break;
      default:
        break;
    }
  }

  private dispatch(out: SseMessage[]): void {
    if (this.dataLines.length === 0 && !this.eventName) return;
    out.push({ event: this.eventName || 'message', data: this.dataLines.join('\n'), id: this.lastId });
    this.eventName = '';
    this.dataLines = [];
  }
}

/** Convert a raw SSE message to a typed StreamEvent. Returns null for pings and unparsable frames. */
export function toStreamEvent(msg: SseMessage): StreamEvent | null {
  if (msg.event === 'ping') return null;
  if (!msg.data) return null;
  try {
    const parsed = JSON.parse(msg.data) as { type?: string };
    const type = parsed.type ?? msg.event;
    if (type === 'ping') return null;
    return { ...parsed, type } as StreamEvent;
  } catch {
    return null;
  }
}

/** Async iterator over the typed events in a `text/event-stream` body. */
export async function* readStreamEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<StreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const msg of parser.push(decoder.decode(value, { stream: true }))) {
        const ev = toStreamEvent(msg);
        if (ev) yield ev;
      }
    }
    for (const msg of parser.push(decoder.decode())) {
      const ev = toStreamEvent(msg);
      if (ev) yield ev;
    }
    for (const msg of parser.flush()) {
      const ev = toStreamEvent(msg);
      if (ev) yield ev;
    }
  } finally {
    reader.releaseLock();
  }
}
